import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  {
    label: "Services", path: "/services",
    children: [
      { label: "Capital Raising", path: "/services/capital-raising" },
      { label: "Asset Management", path: "/services/asset-management" },
      { label: "Strategic Advisory", path: "/services/strategic-advisory" },
    ],
  },
  { label: "Sectors", path: "/sectors" },
  { label: "Team", path: "/team" },
  { label: "Insights", path: "/insights" },
  { label: "Case Studies", path: "/case-studies" },
  { label: "Contact", path: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { getPage } = useContent();
  const brand = getPage("branding", {});

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setActiveDropdown(null); }, [location]);

  const isHeroPage = location.pathname === "/";
  const companyName = brand.company_name || "Capstone";
  const tagline = brand.tagline !== undefined ? brand.tagline : "Asset & Capital";
  const logoSize = brand.logo_size || "medium";
  const logoSizeClass = { small: "h-8", medium: "h-10", large: "h-12", xlarge: "h-14", xxlarge: "h-16", xxxlarge: "h-[4.5rem]" }[logoSize] || "h-10";

  return (
    <header data-testid="main-header" className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled || !isHeroPage ? "backdrop-blur-xl bg-white/90 border-b border-gray-100 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={companyName} className={`${logoSizeClass} w-auto object-contain`} />
            ) : (
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${scrolled || !isHeroPage ? 'bg-navy-900' : 'bg-white/20 backdrop-blur-sm'}`}>
                <span className="text-white font-serif font-semibold text-lg">{companyName[0]}</span>
              </div>
            )}
            <div>
              <span className={`font-serif text-lg tracking-tight font-semibold ${scrolled || !isHeroPage ? 'text-navy-900' : 'text-white'}`}>{companyName}</span>
              {tagline && <span className={`hidden sm:inline font-sans text-xs tracking-[0.15em] uppercase ml-2 ${scrolled || !isHeroPage ? 'text-gray-500' : 'text-white/70'}`}>{tagline}</span>}
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <div key={link.path} className="relative" onMouseEnter={() => link.children && setActiveDropdown(link.path)} onMouseLeave={() => setActiveDropdown(null)}>
                <Link to={link.path} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`} className={`flex items-center gap-1 px-4 py-2 text-sm font-sans font-medium transition-colors duration-200 ${scrolled || !isHeroPage ? location.pathname === link.path || location.pathname.startsWith(link.path + '/') ? 'text-gold-500' : 'text-gray-700 hover:text-navy-900' : location.pathname === link.path ? 'text-gold-400' : 'text-white/80 hover:text-white'}`}>
                  {link.label}{link.children && <ChevronDown className="w-3 h-3" />}
                </Link>
                {link.children && activeDropdown === link.path && (
                  <div className="absolute top-full left-0 pt-2 w-56">
                    <div className="bg-white border border-gray-100 shadow-lg rounded-sm py-2">
                      {link.children.map((child) => (
                        <Link key={child.path} to={child.path} data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`} className="block px-5 py-2.5 text-sm text-gray-600 hover:text-navy-900 hover:bg-gray-50 transition-colors font-sans">{child.label}</Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/contact" data-testid="header-cta-button" className={`hidden md:inline-flex items-center px-6 py-2.5 text-sm font-sans font-medium transition-all duration-300 rounded-sm ${scrolled || !isHeroPage ? 'bg-navy-900 text-white hover:bg-navy-800' : 'border border-white/40 text-white hover:bg-white/10'}`}>Schedule a Consultation</Link>
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className={`lg:hidden p-2 ${scrolled || !isHeroPage ? 'text-navy-900' : 'text-white'}`}>{mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="max-w-7xl mx-auto px-6 py-6 space-y-1">
            {NAV_LINKS.map((link) => (
              <div key={link.path}>
                <Link to={link.path} className="block px-4 py-3 text-gray-700 hover:text-navy-900 font-sans text-sm font-medium">{link.label}</Link>
                {link.children && <div className="pl-8 space-y-1">{link.children.map((child) => <Link key={child.path} to={child.path} className="block px-4 py-2 text-gray-500 hover:text-navy-900 text-sm font-sans">{child.label}</Link>)}</div>}
              </div>
            ))}
            <Link to="/contact" className="block mx-4 mt-4 text-center bg-navy-900 text-white py-3 rounded-sm text-sm font-sans font-medium">Schedule a Consultation</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
